import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ComponentType,
  type ReactNode,
} from 'react';
import type {
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  RowGroupOpenedEvent,
} from 'ag-grid-community';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import type { AgGridFetchFunction } from '@/app/common/components/ag-grid/types';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import type { TopicOverview } from '../../types';
import { topicsColumnConfig } from '../../config/topicsColumnConfig';

type AgGridWrapperProps = {
  table_name?: string;
  height?: string;
  columns: unknown[];
  data: unknown[];
  total?: number;
  fetchFunction?: AgGridFetchFunction;
  additionalData?: Record<string, unknown>;
  settings?: Record<string, unknown>;
  otherOptions?: Record<string, unknown>;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

function TopicDetailCellRenderer(
  props: ICellRendererParams<TopicOverview> & {
    renderExpandedContent?: (topic: TopicOverview) => ReactNode;
  },
) {
  const topic = props.data;
  if (!topic || !props.renderExpandedContent) return null;
  return <div className="overflow-auto">{props.renderExpandedContent(topic)}</div>;
}

export function TopicsTable({
  topics,
  total,
  fetchFunction,
  additionalData,
  loading,
  expandedTopicId,
  onToggleTopic,
  onDeleteTopic,
  onExpandedChange,
  renderExpandedContent,
  emptyMessage,
}: {
  /** First page (page 1, default sort); later pages/sorts go through `fetchFunction`. */
  topics: TopicOverview[];
  total: number;
  fetchFunction: AgGridFetchFunction;
  additionalData: Record<string, unknown>;
  loading?: boolean;
  expandedTopicId: string | null;
  onToggleTopic: (topicId: string) => void;
  onDeleteTopic: (topic: TopicOverview) => void;
  onExpandedChange: (topicId: string | null) => void;
  renderExpandedContent?: (topic: TopicOverview) => ReactNode;
  emptyMessage: ReactNode;
}) {
  const gridApiRef = useRef<GridApi | null>(null);
  const syncingRef = useRef(false);
  const expandedTopicIdRef = useRef(expandedTopicId);
  expandedTopicIdRef.current = expandedTopicId;

  const columns = useMemo(
    () => topicsColumnConfig({ expandedTopicId, onToggleTopic, onDeleteTopic }),
    [expandedTopicId, onToggleTopic, onDeleteTopic],
  );

  const syncExpandedRows = useCallback((api: GridApi, topicId: string | null) => {
    syncingRef.current = true;
    api.forEachNode(node => {
      const shouldExpand = Boolean(node.data && node.data.id === topicId);
      if (node.expanded !== shouldExpand) {
        node.setExpanded(shouldExpand);
      }
    });
    syncingRef.current = false;
  }, []);

  useEffect(() => {
    const api = gridApiRef.current;
    if (!api) return;
    syncExpandedRows(api, expandedTopicId);
  }, [expandedTopicId, topics, syncExpandedRows]);

  const otherOptions = useMemo(
    () => ({
      masterDetail: true,
      detailCellRenderer: TopicDetailCellRenderer,
      detailCellRendererParams: {
        renderExpandedContent,
      },
      detailRowAutoHeight: true,
      isRowMaster: () => true,
      getRowId: (params: { data: TopicOverview }) => params.data.id,
      getRowStyle: (params: { data?: TopicOverview }) =>
        params.data && params.data.id === expandedTopicId
          ? { background: 'rgba(44, 123, 229, 0.08)' }
          : undefined,
      onGridReady: (event: GridReadyEvent) => {
        gridApiRef.current = event.api;
        syncExpandedRows(event.api, expandedTopicIdRef.current);
      },
      onRowGroupOpened: (event: RowGroupOpenedEvent<TopicOverview>) => {
        if (syncingRef.current || !event.data) return;
        if (event.expanded) {
          onExpandedChange(event.data.id);
        } else if (expandedTopicIdRef.current === event.data.id) {
          onExpandedChange(null);
        }
      },
    }),
    [expandedTopicId, onExpandedChange, renderExpandedContent, syncExpandedRows],
  );

  if (loading) {
    return <AgGridSkeleton height="500px" />;
  }

  if (!topics.length) {
    return <div className="py-5 text-center fs--1 text-500">{emptyMessage}</div>;
  }

  return (
    <div className="w-100 ag-grid-in-card">
      <TypedAgGridWrapper
        table_name="tracker_ai_topics"
        height="auto"
        columns={columns}
        data={topics}
        total={total}
        fetchFunction={fetchFunction}
        additionalData={additionalData}
        settings={{
          pagination: true,
          fullscreen: false,
          sideBar: false,
          masterDetail: true,
          export: { enabled: false },
        }}
        otherOptions={otherOptions}
      />
    </div>
  );
}
